import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { format, parseISO, addDays, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Users,
  Anchor,
  Calendar,
  Filter,
  Ticket,
  ChevronDown,
  ChevronUp,
  Wallet,
  CreditCard
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import {
  agruparVentasPorMedio,
  calcularTotalesFinancieros,
  combinarDesglosesMedios,
  combinarTotalesFinancieros
} from '../../utils/pagos';

const obtenerDiasRegistrados = (valor) => {
  const numero = Number(valor);
  if (!Number.isFinite(numero) || numero <= 0) {
    return 1;
  }
  return Math.floor(numero);
};

const intentarParseJSON = (valor) => {
  if (valor == null) return null;
  if (typeof valor !== 'string') return valor;

  const texto = valor.trim();
  if (!texto) return null;

  try {
    return JSON.parse(texto);
  } catch (error) {
    return texto;
  }
};

const registrarFecha = (set, valor) => {
  if (valor == null) return;

  const agregar = (fecha) => {
    if (fecha && isValid(fecha)) {
      set.add(format(fecha, 'yyyy-MM-dd'));
    }
  };

  if (valor instanceof Date) {
    agregar(valor);
    return;
  }

  if (typeof valor === 'number') {
    const fecha = new Date(valor);
    agregar(fecha);
    return;
  }

  if (typeof valor === 'string') {
    const texto = valor.trim();
    if (!texto) return;

    const coincidencias = texto.match(/\d{4}-\d{2}-\d{2}/g);
    if (coincidencias) {
      coincidencias.forEach((match) => {
        const fecha = new Date(`${match}T00:00:00`);
        agregar(fecha);
      });
      if (coincidencias.length > 0) return;
    }

    const parsedIso = parseISO(texto);
    if (isValid(parsedIso)) {
      agregar(parsedIso);
      return;
    }

    const generica = new Date(texto);
    if (isValid(generica)) {
      agregar(generica);
      return;
    }

    texto
      .split(/[;,\n]+/)
      .map((parte) => parte.trim())
      .filter(Boolean)
      .forEach((parte) => registrarFecha(set, parte));
    return;
  }

  if (Array.isArray(valor)) {
    valor.forEach((item) => registrarFecha(set, item));
    return;
  }

  if (typeof valor === 'object') {
    Object.values(valor).forEach((item) => registrarFecha(set, item));
  }
};

const obtenerDiasVenta = (registro = {}) => {
  const dias = new Set();

  const detalleNormalizado = intentarParseJSON(
    registro.detalle ?? registro.detalles ?? registro.diasDetalle ?? null
  );

  registrarFecha(dias, detalleNormalizado);

  const fechaBaseCruda =
    registro.fecha ?? registro.createdAt ?? registro.fecha_emision ?? null;

  if (dias.size === 0 && fechaBaseCruda) {
    const fechaBase = new Date(fechaBaseCruda);
    if (isValid(fechaBase)) {
      const diasEstadia = Math.max(
        1,
        Number(registro.diasEstadia ?? registro.dias_estadia ?? 1) || 1
      );
      for (let i = 0; i < diasEstadia; i += 1) {
        const diaActual = addDays(fechaBase, i);
        if (isValid(diaActual)) {
          dias.add(format(diaActual, 'yyyy-MM-dd'));
        }
      }
    }
  }

  if (dias.size === 0 && registro.validoHasta) {
    const validoHasta = new Date(registro.validoHasta);
    if (isValid(validoHasta)) {
      dias.add(format(validoHasta, 'yyyy-MM-dd'));
    }
  }

  if (dias.size === 0) {
    dias.add(format(new Date(), 'yyyy-MM-dd'));
  }

  return dias;
};

const normalizarRegistroHistorico = (registro = {}) => {
  const extrasRegistro = (registro.extras || []).map((extra) => ({
    ...extra,
    cantidad: Number(extra.cantidad || 0),
    valor_unitario: Number(extra.valor_unitario || 0),
    total: Number(extra.total || 0),
    extra: extra.extra
      ? {
          ...extra.extra,
          valor: Number(extra.extra.valor || 0),
        }
      : null,
  }));

  const extrasSubtotal = extrasRegistro.reduce(
    (sum, extra) => sum + Number(extra.total || 0),
    0
  );

  const extrasCantidad = extrasRegistro.reduce(
    (sum, extra) => sum + Number(extra.cantidad || 0),
    0
  );

  const diasVenta = obtenerDiasVenta(registro);
  const diasDetalle = Array.from(diasVenta);

  const totalVentasNumero = Number(registro.totalVentas || 0);
  const totalBaseCalculado = totalVentasNumero - extrasSubtotal;
  const totalClientesNumero = Number(registro.totalClientes || 0);

  return {
    ...registro,
    anulado: Boolean(registro.anulado),
    fechaAnulacion: registro.fechaAnulacion || registro.fecha_anulacion || null,
    monto_medio_pago_1: Number(registro.monto_medio_pago_1 || 0),
    monto_medio_pago_2: Number(registro.monto_medio_pago_2 || 0),
    diasEstadia: registro.diasEstadia || registro.dias_estadia || 1,
    validoHasta: registro.validoHasta || registro.valido_hasta || null,
    extras: extrasRegistro,
    extrasSubtotal: Number(extrasSubtotal.toFixed(2)),
    extrasCantidad: Number(extrasCantidad.toFixed(0)),
    totalClientes: totalClientesNumero,
    totalVentas: totalVentasNumero,
    diasDetalle,
    diasUnicos: diasDetalle.length,
    totalBase: registro.totalBase != null
      ? Number(registro.totalBase)
      : Number(Math.max(totalBaseCalculado, 0).toFixed(2)),
  };
};

// Función para formatear medios de pago (moved outside components)
const formatMediosPago = (registro) => {
  const medios = [];
  if (registro.medio_pago?.nombre) medios.push(registro.medio_pago.nombre);
  if (registro.medio_pago_2?.nombre) medios.push(registro.medio_pago_2.nombre);
  return medios.join(' + ') || 'No especificado';
};

const Button = ({ children, className, ...props }) => (
  <button className={`px-4 py-2 rounded-md ${className}`} {...props}>
    {children}
  </button>
);

const Card = ({ children, className }) => (
  <div className={`rounded-lg shadow-lg ${className}`}>
    {children}
  </div>
);

const DiaRegistro = ({ dia, onDownload, onShowBreakdown }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalesFinancieros = dia.totalesFinancieros || {
    entradas: 0,
    extras: Number(dia.totalExtras || 0),
    totalGeneral: Number(dia.totalVentas || 0),
  };
  const mediosDia = dia.desgloseMedios || { detalle: [], totales: {} };
  const totalesMediosDia = mediosDia.totales || {};
  const efectivoDia = totalesMediosDia.efectivo || { total: 0, entradas: 0, extras: 0 };
  const otrosMediosDia = totalesMediosDia.otros || { total: 0, entradas: 0, extras: 0 };
  const detalleMediosDia = Array.isArray(mediosDia.detalle) ? mediosDia.detalle : [];

  return (
    <motion.div
      className="bg-[#1B4F72] bg-opacity-10 rounded-lg p-6 mb-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div
          className="flex items-center space-x-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <ChevronUp className="text-[#F1C40F]" size={20} />
          ) : (
            <ChevronDown className="text-[#F1C40F]" size={20} />
          )}
          <h2 className="text-xl font-bold text-[#F1C40F]">
            {format(parseISO(dia.fecha), 'EEEE, d MMMM yyyy', { locale: es })}
          </h2>
        </div>

        <div className="flex items-center space-x-4">
          <Button
            onClick={() => onDownload(dia)}
            className="button-secondary"
          >
            <Download className="mr-2" size={18} />
            Descargar día
          </Button>
        </div>
      </div>

      {/* Resumen del día (siempre visible) */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 my-4">
        <div className="bg-[#1B4F72] bg-opacity-30 p-3 rounded-lg">
          <p className="text-md mb-1 text-[#BDC3C7] flex items-center justify-center">
            <Users className="mr-2" size={16} /> Clientes
          </p>
          <p className="text-2xl font-bold text-center text-[#3498DB]">
            {dia.totalClientes}
          </p>
          {dia.totalClientesAnulados > 0 && (
            <p className="text-xs text-center text-red-300 mt-1">
              Anuladas: {dia.totalClientesAnulados}
            </p>
          )}
        </div>

        <div className="bg-[#1B4F72] bg-opacity-30 p-3 rounded-lg">
          <p className="text-md mb-1 text-[#BDC3C7] flex items-center justify-center">
            <DollarSign className="mr-2" size={16} /> Entradas
          </p>
          <p className="text-2xl font-bold text-center text-[#F1C40F]">
            $
            {Number(totalesFinancieros.entradas || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
          <p className="text-xs text-center text-[#BDC3C7]">
            Sin extras incluidos
          </p>
        </div>

        <div className="bg-[#1B4F72] bg-opacity-30 p-3 rounded-lg">
          <p className="text-md mb-1 text-[#BDC3C7] flex items-center justify-center">
            <Wallet className="mr-2" size={16} /> Total ingresos
          </p>
          <p className="text-2xl font-bold text-center text-[#F1C40F]">
            $
            {Number(
              totalesFinancieros.totalGeneral || dia.totalVentas || 0
            ).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </p>
          {dia.totalVentasAnulados > 0 && (
            <p className="text-xs text-center text-red-300 mt-1">
              Anulados: $
              {Number(dia.totalVentasAnulados || 0).toLocaleString('es-AR', {
                minimumFractionDigits: 2,
              })}
            </p>
          )}
        </div>

        <div className="bg-[#1B4F72] bg-opacity-30 p-3 rounded-lg">
          <p className="text-md mb-1 text-[#BDC3C7] flex items-center justify-center">
            <Anchor className="mr-2" size={16} /> Embarcaciones
          </p>
          <p className="text-2xl font-bold text-center text-[#2ECC71]">
            {dia.totalEmbarcaciones}
          </p>
        </div>

        <div className="bg-[#1B4F72] bg-opacity-30 p-3 rounded-lg">
          <p className="text-md mb-1 text-[#BDC3C7] flex items-center justify-center">
            Días vendidos
          </p>
          <p className="text-2xl font-bold text-center text-[#00F7FF]">
            {Number(dia.totalDias || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 0,
            })}
          </p>
          {Number(dia.totalDiasAnulados || 0) > 0 && (
            <p className="text-xs text-center text-red-300 mt-1">
              Anulados:{' '}
              {Number(dia.totalDiasAnulados || 0).toLocaleString('es-AR', {
                minimumFractionDigits: 0,
              })}
            </p>
          )}
        </div>

        <div className="bg-[#1B4F72] bg-opacity-30 p-3 rounded-lg">
          <p className="text-md mb-1 text-[#BDC3C7] flex items-center justify-center">
            Extras
          </p>
          <p className="text-2xl font-bold text-center text-[#F39C12]">
            $
            {Number(dia.totalExtras || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
          <p className="text-xs text-center text-[#BDC3C7]">
            Ingresos por extras: $
            {Number(totalesFinancieros.extras || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
          {Number(dia.totalExtrasAnulados || 0) > 0 && (
            <p className="text-xs text-center text-red-300 mt-1">
              Anulados: $
              {Number(dia.totalExtrasAnulados || 0).toLocaleString('es-AR', {
                minimumFractionDigits: 2,
              })}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 mb-4">
        <div className="bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg text-center">
          <p className="text-md text-[#BDC3C7] flex items-center justify-center mb-1">
            <DollarSign className="mr-2" size={16} /> Total en efectivo
          </p>
          <p className="text-xl font-bold text-[#F1C40F]">
            $
            {Number(efectivoDia.total || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
          <p className="text-xs text-[#BDC3C7] mt-1">
            Entradas: $
            {Number(efectivoDia.entradas || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}{' '}
            · Extras: $
            {Number(efectivoDia.extras || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
        </div>
        <div className="bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg text-center">
          <p className="text-md text-[#BDC3C7] flex items-center justify-center mb-1">
            <CreditCard className="mr-2" size={16} /> Otros medios
          </p>
          <p className="text-xl font-bold text-[#3498DB]">
            $
            {Number(otrosMediosDia.total || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
          <p className="text-xs text-[#BDC3C7] mt-1">
            Entradas: $
            {Number(otrosMediosDia.entradas || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}{' '}
            · Extras: $
            {Number(otrosMediosDia.extras || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
            })}
          </p>
        </div>
      </div>

      {/* Detalles expandibles */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            {/* Nueva sección de detalle de ventas */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-[#F1C40F]">
                Desglose por medios de pago
              </h3>
              {detalleMediosDia.length === 0 ? (
                <p className="text-sm text-[#BDC3C7]">
                  No hay información de medios de pago registrada para este día.
                </p>
              ) : (
                <div className="space-y-2">
                  {detalleMediosDia.map((medio) => (
                    <div
                      key={medio.id ?? medio.nombre}
                      className="flex flex-col md:flex-row md:items-center md:justify-between bg-[#1B4F72] bg-opacity-30 rounded-lg px-4 py-3 text-sm"
                    >
                      <span className="font-semibold text-[#F1C40F]">
                        {medio.nombre}
                      </span>
                      <div className="flex flex-col md:flex-row md:items-center md:space-x-4 text-[#BDC3C7]">
                        <span>
                          Total: $
                          {Number(medio.total || medio.monto || 0).toLocaleString(
                            'es-AR',
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                        <span>
                          Entradas: $
                          {Number(medio.entradasMonto || 0).toLocaleString(
                            'es-AR',
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                        <span>
                          Extras: $
                          {Number(medio.extrasMonto || medio.extras || 0).toLocaleString(
                            'es-AR',
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                        {Number(medio.totalAnulado || medio.montoAnulado || 0) >
                          0 && (
                          <span className="text-red-300">
                            Anulados: $
                            {Number(
                              medio.totalAnulado || medio.montoAnulado || 0
                            ).toLocaleString('es-AR', {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-[#3498DB]">
                Detalle de Ventas del Día
              </h3>

              <div className="overflow-x-auto">
                <div className="grid grid-cols-13 gap-1 font-semibold text-[#BDC3C7] pb-2 mb-2 border-b border-[#BDC3C7] border-opacity-20 text-sm min-w-[900px]">
                  <div className="col-span-1 px-1">Ticket</div>
                  <div className="col-span-1 px-1">Hora</div>
                  <div className="col-span-2 px-1">Usuario</div>
                  <div className="col-span-1 px-1 text-center">Entradas</div>
                  <div className="col-span-2 px-1 text-right">Total</div>
                  <div className="col-span-2 px-1">Medios de Pago</div>
                  <div className="col-span-2 px-1 text-right">Monto 1</div>
                  <div className="col-span-2 px-1 text-right">Monto 2</div>
                </div>

                {dia.registros.map((registro) => {
                  const esAnulado = Boolean(registro.anulado);
                  const fechaRegistro = registro.createdAt
                    ? new Date(registro.createdAt)
                    : null;
                  const fechaAnulacion =
                    registro.fechaAnulacion || registro.fecha_anulacion || null;
                  const fechaAnulacionDate = fechaAnulacion
                    ? new Date(fechaAnulacion)
                    : null;
                  const fechaAnulacionFormat = fechaAnulacionDate
                    ? format(fechaAnulacionDate, 'dd/MM/yyyy HH:mm')
                    : null;

                  return (
                    <motion.div
                      key={registro.id}
                      className={`grid grid-cols-13 gap-1 items-center py-2 text-sm rounded-lg transition-colors min-w-[900px] ${
                        esAnulado
                          ? 'bg-red-900/20 border border-red-500/40 text-red-200'
                          : 'hover:bg-[#1B4F72] hover:bg-opacity-20'
                      }`}
                      title={
                        esAnulado && fechaAnulacionFormat
                          ? `Anulada el ${fechaAnulacionFormat}`
                          : undefined
                      }
                    >
                      <div className="col-span-1 px-1 font-semibold">#{registro.id}</div>
                      <div className="col-span-1 px-1">
                        {fechaRegistro
                          ? format(fechaRegistro, 'HH:mm')
                          : '--:--'}
                      </div>
                      <div className="col-span-2 px-1 truncate flex items-center space-x-2">
                        <span>
                          {registro.usuario?.nombre ||
                            `ID: ${registro.usuario_id}`}
                        </span>
                        {esAnulado && (
                          <span className="px-2 py-0.5 text-xs uppercase tracking-wide bg-red-500/20 border border-red-500/40 rounded-full">
                            Anulada
                          </span>
                        )}
                      </div>
                      <div className="col-span-1 px-1 text-center">
                        {registro.totalClientes}
                      </div>
                      <div
                        className={`col-span-2 px-1 text-right font-bold ${
                          esAnulado
                            ? 'text-red-300 line-through'
                            : 'text-[#F1C40F]'
                        }`}
                      >
                        ${Number(registro.totalVentas)}
                      </div>
                      <div className="col-span-2 px-1 truncate">
                        {formatMediosPago(registro)}
                      </div>

                      <div
                        className={`col-span-2 px-1 text-right font-extrabold text-[1.1em] tracking-wide neon-effect-azul bg-gradient-to-r from-[#1B4F72]/30 to-transparent px-2 py-1 rounded-lg border-l-4 border-[#00F7FF]/50 shadow-[inset_0_0_10px_rgba(0,247,255,0.3)] ${
                          esAnulado
                            ? 'line-through text-red-200 border-red-400/40 shadow-none'
                            : 'text-[#00F7FF]'
                        }`}
                      >
                        ${Number(registro.monto_medio_pago_1 || 0)}
                      </div>
                      <div
                        className={`col-span-2 px-1 text-right font-extrabold text-[1.1em] tracking-wide neon-effect-verde bg-gradient-to-l from-[#27AE60]/30 to-transparent px-2 py-1 rounded-lg border-r-4 border-[#00FF88]/50 shadow-[inset_0_0_10px_rgba(0,255,136,0.3)] ${
                          esAnulado
                            ? 'line-through text-red-200 border-red-400/40 shadow-none'
                            : 'text-[#00FF88]'
                        }`}
                      >
                        ${Number(registro.monto_medio_pago_2 || 0)}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Ventas por usuario */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3 text-[#3498DB]">
                Ventas por Usuario
              </h3>
              <div className="space-y-3">
                {dia.usuarios.map((usuario) => (
                  <div
                    key={usuario.id}
                    className="flex justify-between items-center bg-[#1B4F72] bg-opacity-20 p-3 rounded-lg hover:bg-opacity-30 transition-colors"
                  >
                    <span className="text-md font-medium">{usuario.nombre}</span>
                    <div className="flex items-center space-x-3">
                      <span className="text-md font-bold text-[#3498DB]">
                        {usuario.totalClientes} entradas
                      </span>
                      <span className="text-md font-bold text-[#00F7FF]">
                        {Number(usuario.totalDias || 0).toLocaleString(
                          'es-AR'
                        )}{' '}
                        días
                      </span>
                      <span className="text-md font-bold text-[#F1C40F]">
                        $
                        {Number(
                          usuario.totalVentas || 0
                        ).toLocaleString('es-AR', {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      <span className="text-sm text-[#F1C40F]">
                        Extras: $
                        {Number(
                          usuario.totalExtras || 0
                        ).toLocaleString('es-AR', {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      {usuario.totalClientesAnulados > 0 && (
                        <span className="text-sm text-red-300">
                          Anuladas: {usuario.totalClientesAnulados} ($
                          {Number(
                            usuario.totalVentasAnuladas || 0
                          ).toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                          })}
                          )
                        </span>
                      )}
                      {Number(usuario.totalDiasAnulados || 0) > 0 && (
                        <span className="text-sm text-red-300">
                          Días anulados:{' '}
                          {Number(
                            usuario.totalDiasAnulados || 0
                          ).toLocaleString('es-AR')}
                        </span>
                      )}
                      {Number(usuario.totalExtrasAnulados || 0) > 0 && (
                        <span className="text-sm text-red-300">
                          Extras anulados: $
                          {Number(
                            usuario.totalExtrasAnulados || 0
                          ).toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      )}
                      <Button
                        onClick={() =>
                          onShowBreakdown(usuario.id, dia.fecha)
                        }
                        className="button-secondary text-sm py-1 px-2"
                      >
                        Ver desglose
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const HistoricoRegistros = () => {
  const [registros, setRegistros] = useState([]);
  const [resumenMes, setResumenMes] = useState({
    totalClientes: 0,
    totalVentas: 0,
    totalEmbarcaciones: 0,
    totalClientesAnulados: 0,
    totalVentasAnulados: 0,
    totalDias: 0,
    totalDiasAnulados: 0,
    totalExtras: 0,
    totalExtrasAnulados: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear()
  );
  const [fechaInicioRango, setFechaInicioRango] = useState('');
  const [fechaFinRango, setFechaFinRango] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [ventasUsuarioMedioPago, setVentasUsuarioMedioPago] = useState({
    detalle: [],
    totales: {
      entradas: 0,
      total: 0,
      montoTotal: 0,
      entradasAnuladas: 0,
      totalAnulado: 0,
      dias: 0,
      diasAnulados: 0,
      extras: 0,
      extrasAnulados: 0,
    },
  });
  const [usuarioSeleccionadoDesglose, setUsuarioSeleccionadoDesglose] =
    useState(null);
  const [fechaSeleccionadaDesglose, setFechaSeleccionadaDesglose] =
    useState(null);
  const totalesFinancierosResumen = resumenMes.totalesFinancieros || {
    entradas: 0,
    extras: 0,
    totalGeneral: Number(resumenMes.totalVentas || 0),
    extrasCantidad: 0,
  };
  const mediosResumenMes = resumenMes.mediosDePago || {
    detalle: [],
    totales: {},
  };
  const efectivoResumenMes =
    mediosResumenMes.totales?.efectivo || {
      total: 0,
      entradas: 0,
      extras: 0,
    };
  const otrosResumenMes =
    mediosResumenMes.totales?.otros || {
      total: 0,
      entradas: 0,
      extras: 0,
    };
  const rangoIncompleto = !fechaInicioRango || !fechaFinRango;
  const rangoInvalido =
    !rangoIncompleto &&
    parseISO(fechaInicioRango) > parseISO(fechaFinRango);
  const registrosEnRango = registros.filter((dia) => {
    if (rangoIncompleto || rangoInvalido) {
      return false;
    }

    const fechaDia = parseISO(dia.fecha);
    const inicio = parseISO(fechaInicioRango);
    const fin = parseISO(fechaFinRango);

    return fechaDia >= inicio && fechaDia <= fin;
  });

  useEffect(() => {
    setFechaInicioRango('');
    setFechaFinRango('');
  }, [selectedMonth, selectedYear]);

  // Obtener años disponibles
  useEffect(() => {
    const fetchAniosDisponibles = async () => {
      try {
        const response = await api.get('/registros/anios');
        setAvailableYears(response.data);
        if (
          response.data.length > 0 &&
          !response.data.includes(selectedYear)
        ) {
          setSelectedYear(response.data[0]);
        }
      } catch (error) {
        console.error('Error al obtener años disponibles:', error);
      }
    };

    fetchAniosDisponibles();
  }, []);

  // Obtener datos históricos
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedYear || !selectedMonth) return;

      setLoading(true);
      try {
        const response = await api.get('/registros/historico', {
          params: { year: selectedYear, month: selectedMonth },
        });

        const registrosPorDia = response.data.registrosPorDia;
        const diasProcesados = Object.entries(registrosPorDia).map(
          ([fecha, dia]) => {
            const usuarios = {};
            const diasActivosSet = new Set();
            const diasAnuladosSet = new Set();
            const registrosNormalizados = (dia.registros || []).map(
              normalizarRegistroHistorico
            );
            const totalClientesActivos = registrosNormalizados
              .filter((registro) => !registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.totalClientes || 0),
                0
              );
            const totalClientesAnulados = registrosNormalizados
              .filter((registro) => registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.totalClientes || 0),
                0
              );
            const totalVentasActivas = registrosNormalizados
              .filter((registro) => !registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.totalVentas || 0),
                0
              );
            const totalVentasAnuladas = registrosNormalizados
              .filter((registro) => registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.totalVentas || 0),
                0
              );
            const totalEmbarcacionesActivas = registrosNormalizados
              .filter((registro) => !registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.totalEmbarcaciones || 0),
                0
              );
            const totalExtrasActivos = registrosNormalizados
              .filter((registro) => !registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.extrasSubtotal || 0),
                0
              );
            const totalExtrasAnulados = registrosNormalizados
              .filter((registro) => registro.anulado)
              .reduce(
                (sum, registro) => sum + Number(registro.extrasSubtotal || 0),
                0
              );

            // Procesar usuarios
            registrosNormalizados.forEach((registro) => {
              const esAnulado = Boolean(registro.anulado);
              const diasDetalle = Array.isArray(registro.diasDetalle)
                ? registro.diasDetalle
                : Array.from(obtenerDiasVenta(registro));
              const destinoSet = esAnulado ? diasAnuladosSet : diasActivosSet;
              diasDetalle.forEach((diaDetalle) => destinoSet.add(diaDetalle));

              if (!usuarios[registro.usuario_id]) {
                usuarios[registro.usuario_id] = {
                  id: registro.usuario_id,
                  nombre: registro.usuario?.nombre || 'Desconocido',
                  totalClientes: 0,
                  totalVentas: 0,
                  totalClientesAnulados: 0,
                  totalVentasAnuladas: 0,
                  totalDias: 0,
                  totalDiasAnulados: 0,
                  totalExtras: 0,
                  totalExtrasAnulados: 0,
                  registros: [],
                  diasActivosSet: new Set(),
                  diasAnuladosSet: new Set(),
                };
              }

              const extrasTotales = Number(registro.extrasSubtotal || 0);
              const usuarioDestino = usuarios[registro.usuario_id];
              const usuarioDiasSet = esAnulado
                ? usuarioDestino.diasAnuladosSet
                : usuarioDestino.diasActivosSet;
              diasDetalle.forEach((diaDetalle) => usuarioDiasSet.add(diaDetalle));

              if (esAnulado) {
                usuarios[registro.usuario_id].totalClientesAnulados +=
                  Number(registro.totalClientes) || 0;
                usuarios[registro.usuario_id].totalVentasAnuladas +=
                  Number(registro.totalVentas) || 0;
                usuarios[registro.usuario_id].totalExtrasAnulados +=
                  extrasTotales;
              } else {
                usuarios[registro.usuario_id].totalClientes +=
                  Number(registro.totalClientes) || 0;
                usuarios[registro.usuario_id].totalVentas +=
                  Number(registro.totalVentas) || 0;
                usuarios[registro.usuario_id].totalExtras +=
                  extrasTotales;
              }
              usuarios[registro.usuario_id].registros.push(
                registro
              );
            });

            const usuariosValores = Object.values(usuarios).map((usuario) => {
              const totalDias = usuario.diasActivosSet.size;
              const totalDiasAnulados = usuario.diasAnuladosSet.size;
              return {
                ...usuario,
                totalDias,
                totalDiasAnulados,
              };
            });
            const totalDiasActivos = diasActivosSet.size;
            const totalDiasAnulados = diasAnuladosSet.size;
            const resumenMediosDia = agruparVentasPorMedio(
              registrosNormalizados
            );
            const totalesFinancierosDia = calcularTotalesFinancieros(
              registrosNormalizados
            );

            return {
              fecha: dia.fecha,
              totalClientes: totalClientesActivos,
              totalVentas: totalVentasActivas,
              totalEmbarcaciones: totalEmbarcacionesActivas,
              totalClientesAnulados,
              totalVentasAnulados: totalVentasAnuladas,
              totalDias: totalDiasActivos,
              totalDiasAnulados,
              totalExtras: totalExtrasActivos,
              totalExtrasAnulados,
              registros: registrosNormalizados,
              usuarios: usuariosValores,
              desgloseMedios: resumenMediosDia,
              totalesFinancieros: totalesFinancierosDia,
            };
          }
        );

        const totalesFinancierosMes =
          combinarTotalesFinancieros(diasProcesados);
        const mediosMensuales =
          combinarDesglosesMedios(diasProcesados);

        const nuevoResumenMes = {
          totalClientes: diasProcesados.reduce(
            (sum, dia) => sum + dia.totalClientes,
            0
          ),
          totalVentas: diasProcesados.reduce(
            (sum, dia) => sum + dia.totalVentas,
            0
          ),
          totalEmbarcaciones: diasProcesados.reduce(
            (sum, dia) => sum + dia.totalEmbarcaciones,
            0
          ),
          totalClientesAnulados: diasProcesados.reduce(
            (sum, dia) => sum + (dia.totalClientesAnulados || 0),
            0
          ),
          totalVentasAnulados: diasProcesados.reduce(
            (sum, dia) => sum + (dia.totalVentasAnulados || 0),
            0
          ),
          totalDias: diasProcesados.reduce(
            (sum, dia) => sum + (dia.totalDias || 0),
            0
          ),
          totalDiasAnulados: diasProcesados.reduce(
            (sum, dia) => sum + (dia.totalDiasAnulados || 0),
            0
          ),
          totalExtras: diasProcesados.reduce(
            (sum, dia) => sum + (dia.totalExtras || 0),
            0
          ),
          totalExtrasAnulados: diasProcesados.reduce(
            (sum, dia) => sum + (dia.totalExtrasAnulados || 0),
            0
          ),
          totalesFinancieros: totalesFinancierosMes,
          mediosDePago: mediosMensuales,
        };

        setResumenMes(nuevoResumenMes);
        setRegistros(diasProcesados);
      } catch (error) {
        console.error('Error al obtener registros históricos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedMonth]);

  const cambiarMes = (incremento) => {
    const newDate = new Date(
      selectedYear,
      selectedMonth - 1 + incremento,
      1
    );
    setSelectedMonth(newDate.getMonth() + 1);
    setSelectedYear(newDate.getFullYear());
  };

  // Construir detalle local cuando la API no devuelva datos
  const construirDetallesLocales = (usuario) => {
    if (!usuario || !usuario.registros || !usuario.registros.length) {
      return [];
    }

    return usuario.registros.flatMap((reg) => {
      const extrasLocal = Array.isArray(reg.extras)
        ? reg.extras.reduce(
            (sum, extra) => sum + Number(extra.total || 0),
            0
          )
        : 0;
      const diasEstadia = obtenerDiasRegistrados(
        reg.diasEstadia ?? reg.dias_estadia ?? 1
      );
      const totalClientes = Number(reg.totalClientes || 0);
      const monto1 = Number(reg.monto_medio_pago_1 || 0);
      const monto2 = Number(reg.monto_medio_pago_2 || 0);
      const baseReferencia1 =
        monto1 || Number(reg.totalVentas || 0);
      const baseReferencia2 = monto2 || 0;
      const totalReferencia =
        baseReferencia1 + baseReferencia2 ||
        Number(reg.totalVentas || 0);

      const proporcion1 =
        totalReferencia > 0 ? baseReferencia1 / totalReferencia : 1;
      const proporcion2 =
        totalReferencia > 0 ? baseReferencia2 / totalReferencia : 0;

      const entradas1 = Math.round(totalClientes * proporcion1);
      const entradas2 = Math.round(totalClientes * proporcion2);
      const dias1 = Number(
        (diasEstadia * proporcion1).toFixed(2)
      );
      const dias2 = Number(
        (diasEstadia * proporcion2).toFixed(2)
      );
      const extras1 = Number(
        (extrasLocal * proporcion1).toFixed(2)
      );
      const extras2 = Number(
        (extrasLocal * proporcion2).toFixed(2)
      );

      const base = {
        medio_pago: {
          nombre:
            reg.medio_pago?.nombre || 'Medio no especificado',
        },
        cantidad_entradas: entradas1,
        total_ventas: reg.monto_medio_pago_1 || reg.totalVentas,
        dias: dias1,
        extras: extras1,
        estado: reg.anulado ? 'Anulada' : 'Activa',
      };

      const resultados = [base];

      if (reg.medio_pago_2) {
        resultados.push({
          medio_pago: {
            nombre:
              reg.medio_pago_2?.nombre ||
              'Medio no especificado',
          },
          cantidad_entradas: entradas2,
          total_ventas: reg.monto_medio_pago_2 || 0,
          dias: dias2,
          extras: extras2,
          estado: reg.anulado ? 'Anulada' : 'Activa',
        });
      }

      return resultados;
    });
  };

  const fetchVentasPorUsuarioMedioPago = async (usuarioId, fecha) => {
    const agregarDetalle = (detalles) => {
      const grouped = {};

      detalles.forEach((item) => {
        const medioNombre =
          item.medio_pago?.nombre || 'Medio no especificado';
        if (!grouped[medioNombre]) {
          grouped[medioNombre] = {
            nombre: medioNombre,
            entradas: 0,
            monto: 0,
            dias: 0,
            extras: 0,
            entradasAnuladas: 0,
            montoAnulado: 0,
            diasAnulados: 0,
            extrasAnulados: 0,
          };
        }

        const monto = Number(item.total_ventas || 0);
        const entradas = Number(item.cantidad_entradas || 0);
        const dias = Number(
          item.dias || item.total_dias || 0
        );
        const extras = Number(
          item.extras || item.total_extras || 0
        );
        if (item.estado === 'Anulada') {
          grouped[medioNombre].montoAnulado += monto;
          grouped[medioNombre].entradasAnuladas += entradas;
          grouped[medioNombre].diasAnulados += dias;
          grouped[medioNombre].extrasAnulados += extras;
        } else {
          grouped[medioNombre].monto += monto;
          grouped[medioNombre].entradas += entradas;
          grouped[medioNombre].dias += dias;
          grouped[medioNombre].extras += extras;
        }
      });

      return Object.values(grouped).map((medio) => ({
        ...medio,
        monto: Number(medio.monto.toFixed(2)),
        extras: Number(medio.extras.toFixed(2)),
        montoAnulado: Number(
          medio.montoAnulado.toFixed(2)
        ),
        extrasAnulados: Number(
          medio.extrasAnulados.toFixed(2)
        ),
      }));
    };

    const dia = registros.find((d) => d.fecha === fecha);
    const usuario = dia?.usuarios?.find(
      (u) => u.id === usuarioId
    );

    const construirTotalesUsuario = (usuarioActual, fallback = {}) => ({
      entradas: Number(
        usuarioActual?.totalClientes ?? fallback.entradas ?? 0
      ),
      total: Number(
        usuarioActual?.totalVentas ??
          fallback.total ??
          fallback.montoTotal ??
          0
      ),
      montoTotal: Number(
        usuarioActual?.totalVentas ??
          fallback.montoTotal ??
          fallback.total ??
          0
      ),
      entradasAnuladas: Number(
        usuarioActual?.totalClientesAnulados ??
          fallback.entradasAnuladas ??
          0
      ),
      totalAnulado: Number(
        usuarioActual?.totalVentasAnuladas ??
          fallback.totalAnulado ??
          fallback.montoAnulado ??
          0
      ),
      dias: Number(
        usuarioActual?.totalDias ?? fallback.dias ?? 0
      ),
      diasAnulados: Number(
        usuarioActual?.totalDiasAnulados ??
          fallback.diasAnulados ??
          0
      ),
      extras: Number(
        usuarioActual?.totalExtras ?? fallback.extras ?? 0
      ),
      extrasAnulados: Number(
        usuarioActual?.totalExtrasAnulados ??
          fallback.extrasAnulados ??
          0
      ),
    });

    const setDesdeUsuarioLocal = () => {
      if (!usuario) return;

      const detallesLocales =
        construirDetallesLocales(usuario);
      const detalleAgrupado =
        agregarDetalle(detallesLocales);

      setVentasUsuarioMedioPago({
        detalle: detalleAgrupado,
        totales: construirTotalesUsuario(usuario),
      });

      setUsuarioSeleccionadoDesglose({
        id: usuario.id,
        nombre: usuario.nombre,
      });
      setFechaSeleccionadaDesglose(fecha);
      setModalAbierto(true);
    };

    try {
      const fechaFormateada = format(
        parseISO(fecha),
        'yyyy-MM-dd'
      );

      const response = await api.get(
        '/registros/detalles_venta',
        {
          params: { usuario_id: usuarioId, fecha: fechaFormateada },
        }
      );

      console.log(
        'API Response for payment methods:',
        response.data
      );

      if (!response.data?.detalle?.length) {
        // Sin detalle desde el backend → usamos local
        setDesdeUsuarioLocal();
      } else {
        const detalleServidor = agregarDetalle(
          response.data.detalle
        );
        const totales = response.data.totales || {};
        setVentasUsuarioMedioPago({
          detalle: detalleServidor,
          totales: construirTotalesUsuario(usuario, totales),
        });

        if (usuario) {
          setUsuarioSeleccionadoDesglose({
            id: usuario.id,
            nombre: usuario.nombre,
          });
        } else {
          setUsuarioSeleccionadoDesglose({
            id: usuarioId,
            nombre: '',
          });
        }
        setFechaSeleccionadaDesglose(fecha);
        setModalAbierto(true);
      }
    } catch (error) {
      console.error(
        'Error fetching payment details:',
        error
      );
      // En error, usamos la información local del día
      setDesdeUsuarioLocal();
    }
  };

  const createDailyRegistrosHeader = (fecha, stats) => {
    const formatCurrency = (value) =>
      `$${Number(value || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
      })}`;
    const emptyRow = Array(15).fill('');

    return [
      ['INFORME DIARIO DE REGISTROS', ...Array(14).fill('')],
      [...emptyRow],
      [
        'Fecha:',
        format(parseISO(fecha), 'dd/MM/yyyy'),
        ...Array(13).fill(''),
      ],
      [
        'Total de Registros:',
        stats.totalRegistros,
        ...Array(13).fill(''),
      ],
      [
        'Clientes activos:',
        stats.clientesActivos,
        '',
        '',
        'Clientes anulados:',
        stats.clientesAnulados,
        ...Array(9).fill(''),
      ],
      [
        'Ventas activas:',
        formatCurrency(stats.ventasActivas),
        '',
        '',
        'Ventas anuladas:',
        formatCurrency(stats.ventasAnuladas),
        ...Array(9).fill(''),
      ],
      [
        'Ingresos por entradas:',
        formatCurrency(stats.ingresosEntradas),
        '',
        '',
        'Ingresos por extras:',
        formatCurrency(stats.ingresosExtras),
        ...Array(9).fill(''),
      ],
      [
        'Total general de ingresos:',
        formatCurrency(stats.totalGeneral),
        '',
        '',
        'Extras vendidos (cantidad):',
        stats.cantidadExtras || 0,
        ...Array(9).fill(''),
      ],
      [
        'Total en efectivo:',
        formatCurrency(stats.efectivoTotal),
        '',
        '',
        'Entradas en efectivo:',
        formatCurrency(stats.efectivoEntradas),
        '',
        '',
        'Extras en efectivo:',
        formatCurrency(stats.efectivoExtras),
        ...Array(5).fill(''),
      ],
      [
        'Total otros medios:',
        formatCurrency(stats.otrosTotal),
        '',
        '',
        'Entradas otros medios:',
        formatCurrency(stats.otrosEntradas),
        '',
        '',
        'Extras otros medios:',
        formatCurrency(stats.otrosExtras),
        ...Array(5).fill(''),
      ],
      [
        'Días activos:',
        stats.diasActivos,
        '',
        '',
        'Días anulados:',
        stats.diasAnulados,
        ...Array(9).fill(''),
      ],
      [
        'Extras activos:',
        formatCurrency(stats.extrasActivos),
        '',
        '',
        'Extras anulados:',
        formatCurrency(stats.extrasAnulados),
        ...Array(9).fill(''),
      ],
      [
        'Fecha de Generación:',
        format(new Date(), 'dd/MM/yyyy HH:mm'),
        ...Array(13).fill(''),
      ],
      [...emptyRow],
    ];
  };

  // Función para aplicar estilos a registros diarios
  const applyDailyRegistrosStyles = (worksheet, headerRows) => {
    // Estilos
    const titleStyle = {
      fill: { fgColor: { rgb: '1E3A8A' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 16 },
      alignment: { horizontal: 'center', vertical: 'center' },
    };

    const headerInfoStyle = {
      fill: { fgColor: { rgb: 'F3F4F6' } },
      font: { color: { rgb: '1F2937' }, bold: true, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center' },
    };

    const valueStyle = {
      fill: { fgColor: { rgb: 'DBEAFE' } },
      font: { color: { rgb: '1E40AF' }, bold: true, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center' },
    };

    const tableHeaderStyle = {
      fill: { fgColor: { rgb: '059669' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 12 },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const dataStyle = {
      font: { color: { rgb: '1F2937' }, sz: 10 },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: 'E5E7EB' } },
        bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
        left: { style: 'thin', color: { rgb: 'E5E7EB' } },
        right: { style: 'thin', color: { rgb: 'E5E7EB' } },
      },
    };

    const numberStyle = {
      fill: { fgColor: { rgb: 'FEF3C7' } },
      font: {
        color: { rgb: '92400E' },
        bold: true,
        sz: 10,
      },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    // Aplicar estilo al título
    if (worksheet['A1']) worksheet['A1'].s = titleStyle;

    // Aplicar estilos al encabezado informativo
    const infoPairs = [
      ['A3', 'B3'],
      ['A4', 'B4'],
      ['A5', 'B5'],
      ['E5', 'F5'],
      ['A6', 'B6'],
      ['E6', 'F6'],
      ['A7', 'B7'],
      ['E7', 'F7'],
      ['A8', 'B8'],
      ['E8', 'F8'],
      ['A9', 'B9'],
    ];

    infoPairs.forEach(([labelCell, valueCell]) => {
      if (worksheet[labelCell]) worksheet[labelCell].s = headerInfoStyle;
      if (worksheet[valueCell]) worksheet[valueCell].s = valueStyle;
    });

    // Merge cells para el título
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } }, // Merge A1:O1
    ];

    // Aplicar estilos a los headers de la tabla
    const headerRowIndex = headerRows + 1;
    const columns = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
    ];
    columns.forEach((col) => {
      if (worksheet[`${col}${headerRowIndex}`]) {
        worksheet[`${col}${headerRowIndex}`].s =
          tableHeaderStyle;
      }
    });

    // Aplicar estilos a los datos
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (
      let row = headerRowIndex + 1;
      row <= range.e.r;
      row++
    ) {
      columns.forEach((col, index) => {
        if (worksheet[`${col}${row}`]) {
          // Aplicar estilo especial a columnas numéricas
          if (
            [3, 5, 6, 7, 8, 9, 10, 13, 14].includes(index)
          ) {
            worksheet[`${col}${row}`].s = numberStyle;
          } else {
            worksheet[`${col}${row}`].s = dataStyle;
          }
        }
      });
    }

    // Configurar ancho de columnas
    worksheet['!cols'] = [
      { wch: 18 }, // Fecha
      { wch: 20 }, // Usuario
      { wch: 15 }, // Medio Pago 1
      { wch: 12 }, // Monto 1
      { wch: 15 }, // Medio Pago 2
      { wch: 12 }, // Monto 2
      { wch: 14 }, // Total Ventas
      { wch: 10 }, // Clientes
      { wch: 10 }, // Días
      { wch: 14 }, // Total Extras
      { wch: 14 }, // Embarcaciones
      { wch: 12 }, // Estado
      { wch: 18 }, // Fecha Anulación
      { wch: 14 }, // Monto Anulado
      { wch: 14 }, // Extras Anulados
    ];

    return worksheet;
  };

  const descargarInformeDiario = (dia) => {
    const diaFormateado = format(
      parseISO(dia.fecha),
      'yyyy-MM-dd'
    );
    const totalRegistros = dia.usuarios.reduce(
      (acc, usuario) => acc + usuario.registros.length,
      0
    );
    const headerStats = {
      totalRegistros,
      clientesActivos: dia.totalClientes || 0,
      clientesAnulados: dia.totalClientesAnulados || 0,
      ventasActivas: Number(dia.totalVentas || 0),
      ventasAnuladas: Number(
        dia.totalVentasAnulados || 0
      ),
      diasActivos: Number(dia.totalDias || 0),
      diasAnulados: Number(dia.totalDiasAnulados || 0),
      extrasActivos: Number(dia.totalExtras || 0),
      extrasAnulados: Number(
        dia.totalExtrasAnulados || 0
      ),
      ingresosEntradas: Number(
        dia.totalesFinancieros?.entradas || 0
      ),
      ingresosExtras: Number(
        dia.totalesFinancieros?.extras ||
          dia.totalExtras ||
          0
      ),
      totalGeneral: Number(
        dia.totalesFinancieros?.totalGeneral ||
          dia.totalVentas ||
          0
      ),
      cantidadExtras: Number(
        dia.totalesFinancieros?.extrasCantidad || 0
      ),
      efectivoTotal: Number(
        dia.desgloseMedios?.totales?.efectivo?.total ||
          0
      ),
      efectivoEntradas: Number(
        dia.desgloseMedios?.totales?.efectivo
          ?.entradas || 0
      ),
      efectivoExtras: Number(
        dia.desgloseMedios?.totales?.efectivo?.extras ||
          0
      ),
      otrosTotal: Number(
        dia.desgloseMedios?.totales?.otros?.total ||
          dia.desgloseMedios?.totales?.otrosMedios
            ?.total ||
          0
      ),
      otrosEntradas: Number(
        dia.desgloseMedios?.totales?.otros?.entradas ||
          dia.desgloseMedios?.totales?.otrosMedios
            ?.entradas ||
          0
      ),
      otrosExtras: Number(
        dia.desgloseMedios?.totales?.otros?.extras ||
          dia.desgloseMedios?.totales?.otrosMedios
            ?.extras ||
          0
      ),
    };

    // Crear encabezado personalizado
    const headerData = createDailyRegistrosHeader(
      dia.fecha,
      headerStats
    );

    const detalleMedios = [
      [
        'DETALLE POR MEDIO DE PAGO',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Medio',
        'Total',
        'Entradas ($)',
        'Extras ($)',
        'Total Anulado',
        'Entradas Anuladas ($)',
        'Extras Anulados ($)',
        'Extras (uds)',
        'Extras anulados (uds)',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      ...(Array.isArray(dia.desgloseMedios?.detalle)
        ? dia.desgloseMedios.detalle.map((medio) => [
            medio.nombre,
            Number(medio.total || medio.monto || 0),
            Number(medio.entradasMonto || 0),
            Number(
              medio.extrasMonto ||
                medio.extras ||
                0
            ),
            Number(
              medio.totalAnulado ||
                medio.montoAnulado ||
                0
            ),
            Number(
              medio.entradasMontoAnulado || 0
            ),
            Number(
              medio.extrasMontoAnulado ||
                medio.extrasAnulados ||
                0
            ),
            Math.round(medio.extrasCantidad || 0),
            Math.round(
              medio.extrasCantidadAnulados || 0
            ),
            '',
            '',
            '',
            '',
            '',
            '',
            '',
          ])
        : []),
      [],
    ];

    // Datos de los registros
    const dataToExport = dia.usuarios.flatMap(
      (usuario) =>
        usuario.registros.map((registro) => {
          const estado = registro.anulado
            ? 'Anulada'
            : 'Activa';
          const fechaAnulacion =
            registro.fechaAnulacion ||
            registro.fecha_anulacion
              ? format(
                  new Date(
                    registro.fechaAnulacion ||
                      registro.fecha_anulacion
                  ),
                  'dd/MM/yyyy HH:mm'
                )
              : '-';

          const extrasSubtotal = Array.isArray(
            registro.extras
          )
            ? registro.extras.reduce(
                (sum, extra) =>
                  sum + Number(extra.total || 0),
                0
              )
            : 0;
          const diasEstadia = obtenerDiasRegistrados(
            registro.diasEstadia ??
              registro.dias_estadia ??
              1
          );

          return {
            Ticket: registro.id ?? registro.ticket ?? '-',
            Fecha: registro.createdAt
              ? format(
                  new Date(registro.createdAt),
                  'dd/MM/yyyy HH:mm'
                )
              : '--',
            Usuario:
              registro.usuario?.nombre ||
              `ID: ${registro.usuario_id}`,
            'Medio Pago 1':
              registro.medio_pago?.nombre || '-',
            'Monto 1': Number(
              registro.monto_medio_pago_1 || 0
            ),
            'Medio Pago 2':
              registro.medio_pago_2?.nombre || '-',
            'Monto 2': Number(
              registro.monto_medio_pago_2 || 0
            ),
            'Total Ventas': Number(
              registro.totalVentas || 0
            ),
            Clientes: Number(
              registro.totalClientes || 0
            ),
            Días: Number(diasEstadia || 0),
            'Total Extras': Number(
              extrasSubtotal || 0
            ),
            Embarcaciones: Number(
              registro.totalEmbarcaciones || 0
            ),
            Estado: estado,
            'Fecha Anulación': fechaAnulacion,
            'Monto Anulado': registro.anulado
              ? Number(registro.totalVentas || 0)
              : 0,
            'Extras Anulados': registro.anulado
              ? Number(extrasSubtotal || 0)
              : 0,
          };
        })
    );

    // Combinar encabezado con datos
    const fullData = [
      ...headerData,
      ...detalleMedios,
      Object.keys(dataToExport[0] || {}), // Headers de la tabla
      ...dataToExport.map((row) => Object.values(row)), // Datos de la tabla
    ];

    // Crear worksheet desde array
    let worksheet = XLSX.utils.aoa_to_sheet(fullData);

    // Aplicar estilos avanzados
    worksheet = applyDailyRegistrosStyles(
      worksheet,
      10
    ); // 10 filas de encabezado

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Registros Diarios'
    );

    // Configurar propiedades del libro
    workbook.Props = {
      Title: `Registros del ${format(
        parseISO(dia.fecha),
        'dd/MM/yyyy'
      )}`,
      Subject: 'Histórico de Registros Diarios',
      Author: 'Driza - Gestión de Campings',
      Manager: 'Departamento de Operaciones',
      Company: 'Marina Club Náutico',
      CreatedDate: new Date(),
    };

    XLSX.writeFile(
      workbook,
      `registros_${diaFormateado}.xlsx`
    );

    // Mostrar notificación de éxito (opcional)
    console.log(
      `Excel generado exitosamente: registros_${diaFormateado}.xlsx`
    );
  };

  const generarInformeRegistros = ({
    registrosFuente,
    periodoTitulo,
    nombreArchivo,
    nombreHoja,
    prefijoTitulo,
  }) => {
    if (!Array.isArray(registrosFuente) || registrosFuente.length === 0) {
      return;
    }

    // Calcular estadísticas del período
    const totalRegistros = registrosFuente.reduce(
      (acc, dia) => acc + dia.registros.length,
      0
    );
    const totalVentasActivas = registrosFuente.reduce(
      (acc, dia) => acc + Number(dia.totalVentas || 0),
      0
    );
    const totalVentasAnuladas = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalVentasAnulados || 0),
      0
    );
    const totalClientesActivos = registrosFuente.reduce(
      (acc, dia) => acc + Number(dia.totalClientes || 0),
      0
    );
    const totalClientesAnulados = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalClientesAnulados || 0),
      0
    );
    const totalDiasActivos = registrosFuente.reduce(
      (acc, dia) => acc + Number(dia.totalDias || 0),
      0
    );
    const totalDiasAnulados = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalDiasAnulados || 0),
      0
    );
    const totalExtrasActivos = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalExtras || 0),
      0
    );
    const totalExtrasAnulados = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalExtrasAnulados || 0),
      0
    );
    const totalesFinancierosPeriodo =
      combinarTotalesFinancieros(registrosFuente);
    const mediosPeriodo =
      combinarDesglosesMedios(registrosFuente);
    const formatCurrency = (value) =>
      `$${Number(value || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
      })}`;

    // Datos del resumen mensual
    const resumenData = [
      [
        prefijoTitulo,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      ['', '', '', '', '', '', '', '', ''],
      [
        'Período:',
        periodoTitulo,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],

      [
        'Fecha de Generación:',
        format(new Date(), 'dd/MM/yyyy HH:mm'),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      ['', '', '', '', '', '', '', '', ''],
      [
        'ESTADÍSTICAS GENERALES',
        'CANTIDAD',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Registros',
        totalRegistros,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Ventas Activas',
        formatCurrency(totalVentasActivas),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Ventas Anuladas',
        formatCurrency(totalVentasAnuladas),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Clientes Activos',
        totalClientesActivos,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Clientes Anulados',
        totalClientesAnulados,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Días Activos',
        totalDiasActivos,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Días Anulados',
        totalDiasAnulados,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Extras Activos',
        formatCurrency(totalExtrasActivos),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total de Extras Anulados',
        formatCurrency(totalExtrasAnulados),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Ingresos por entradas',
        formatCurrency(totalesFinancierosPeriodo.entradas),
        '',
        '',
        'Ingresos por extras',
        formatCurrency(totalesFinancierosPeriodo.extras),
        '',
        '',
        '',
      ],
      [
        'Total general de ingresos',
        formatCurrency(
          totalesFinancierosPeriodo.totalGeneral
        ),
        '',
        '',
        'Cantidad total de extras',
        totalesFinancierosPeriodo.extrasCantidad || 0,
        '',
        '',
        '',
      ],
      [
        'Total en efectivo',
        formatCurrency(
          mediosPeriodo.totales?.efectivo?.total || 0
        ),
        '',
        '',
        'Entradas en efectivo',
        formatCurrency(
          mediosPeriodo.totales?.efectivo?.entradas ||
            0
        ),
        '',
        '',
        '',
      ],
      [
        'Total otros medios',
        formatCurrency(
          mediosPeriodo.totales?.otros?.total || 0
        ),
        '',
        '',
        'Entradas otros medios',
        formatCurrency(
          mediosPeriodo.totales?.otros?.entradas || 0
        ),
        '',
        '',
        '',
      ],
      ['', '', '', '', '', '', '', '', ''],
      ['DETALLE DE REGISTROS', '', '', '', '', '', '', '', ''],
    ];

    // Datos detallados de los registros
    const dataToExport = registrosFuente.flatMap((dia) =>
      dia.registros.map((registro) => {
        const estado = registro.anulado
          ? 'Anulada'
          : 'Activa';
        const fechaAnulacion =
          registro.fechaAnulacion ||
          registro.fecha_anulacion
            ? format(
                new Date(
                  registro.fechaAnulacion ||
                    registro.fecha_anulacion
                ),
                'dd/MM/yyyy HH:mm'
              )
            : '-';

        const extrasSubtotal = Array.isArray(
          registro.extras
        )
          ? registro.extras.reduce(
              (sum, extra) =>
                sum + Number(extra.total || 0),
              0
            )
          : 0;
        const diasEstadia = obtenerDiasRegistrados(
          registro.diasEstadia ??
            registro.dias_estadia ??
            1
        );

        return {
          Ticket: registro.id ?? registro.ticket ?? '-',
          Fecha: registro.createdAt
            ? format(
                new Date(registro.createdAt),
                'dd/MM/yyyy HH:mm'
              )
            : '--',
          Usuario:
            registro.usuario?.nombre ||
            `ID: ${registro.usuario_id}`,
          'Medio Pago 1':
            registro.medio_pago?.nombre || '-',
          'Monto 1': Number(
            registro.monto_medio_pago_1 || 0
          ),
          'Medio Pago 2':
            registro.medio_pago_2?.nombre || '-',
          'Monto 2': Number(
            registro.monto_medio_pago_2 || 0
          ),
          'Total Ventas': Number(
            registro.totalVentas || 0
          ),
          Clientes: Number(
            registro.totalClientes || 0
          ),
          Días: Number(diasEstadia || 0),
          'Total Extras': Number(
            extrasSubtotal || 0
          ),
          Embarcaciones: Number(
            registro.totalEmbarcaciones || 0
          ),
          Estado: estado,
          'Fecha Anulación': fechaAnulacion,
          'Monto Anulado': registro.anulado
            ? Number(registro.totalVentas || 0)
            : 0,
          'Extras Anulados': registro.anulado
            ? Number(extrasSubtotal || 0)
            : 0,
        };
      })
    );

    // Combinar resumen con datos detallados
    const detalleMediosMensual = [
      [
        'DETALLE POR MEDIO DE PAGO',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Medio',
        'Total',
        'Entradas ($)',
        'Extras ($)',
        'Total Anulado',
        'Entradas Anuladas ($)',
        'Extras Anulados ($)',
        'Extras (uds)',
        'Extras anulados (uds)',
      ],
      ...(mediosPeriodo.detalle || []).map(
        (medio) => [
          medio.nombre,
          formatCurrency(medio.total || medio.monto || 0),
          formatCurrency(medio.entradasMonto || 0),
          formatCurrency(
            medio.extrasMonto || medio.extras || 0
          ),
          formatCurrency(
            medio.totalAnulado ||
              medio.montoAnulado ||
              0
          ),
          formatCurrency(
            medio.entradasMontoAnulado || 0
          ),
          formatCurrency(
            medio.extrasMontoAnulado ||
              medio.extrasAnulados ||
              0
          ),
          Math.round(medio.extrasCantidad || 0),
          Math.round(
            medio.extrasCantidadAnulados || 0
          ),
        ]
      ),
      ['', '', '', '', '', '', '', '', ''],
    ];

    const fullData = [
      ...resumenData,
      ...detalleMediosMensual,
      Object.keys(dataToExport[0] || {}), // Headers de la tabla
      ...dataToExport.map((row) => Object.values(row)), // Datos de la tabla
    ];

    // Crear worksheet desde array
    const worksheet = XLSX.utils.aoa_to_sheet(fullData);

    // Estilos personalizados para el resumen mensual
    const titleStyle = {
      fill: { fgColor: { rgb: '1E3A8A' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 16 },
      alignment: { horizontal: 'center', vertical: 'center' },
    };

    const headerInfoStyle = {
      fill: { fgColor: { rgb: 'F3F4F6' } },
      font: { color: { rgb: '1F2937' }, bold: true, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center' },
    };

    const valueStyle = {
      fill: { fgColor: { rgb: 'DBEAFE' } },
      font: { color: { rgb: '1E40AF' }, bold: true, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center' },
    };

    const sectionHeaderStyle = {
      fill: { fgColor: { rgb: '7C3AED' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 14 },
      alignment: { horizontal: 'center', vertical: 'center' },
    };

    const statsHeaderStyle = {
      fill: { fgColor: { rgb: '059669' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 12 },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: {
          style: 'thin',
          color: { rgb: '000000' },
        },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const numberStyle = {
      fill: { fgColor: { rgb: 'FEF3C7' } },
      font: {
        color: { rgb: '92400E' },
        bold: true,
        sz: 11,
      },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: {
          style: 'thin',
          color: { rgb: '000000' },
        },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const dataStyle = {
      font: { color: { rgb: '1F2937' }, sz: 10 },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: 'E5E7EB' } },
        bottom: {
          style: 'thin',
          color: { rgb: 'E5E7EB' },
        },
        left: { style: 'thin', color: { rgb: 'E5E7EB' } },
        right: { style: 'thin', color: { rgb: 'E5E7EB' } },
      },
    };

    // Aplicar estilos al encabezado
    worksheet['A1'].s = titleStyle;
    worksheet['A3'].s = headerInfoStyle;
    worksheet['B3'].s = valueStyle;
    worksheet['A4'].s = headerInfoStyle;
    worksheet['B4'].s = valueStyle;

    // Estilos para la sección de estadísticas
    worksheet['A6'].s = sectionHeaderStyle;
    worksheet['B6'].s = statsHeaderStyle;
    [
      'A7',
      'B7',
      'A8',
      'B8',
      'A9',
      'B9',
      'A10',
      'B10',
      'A11',
      'B11',
      'A12',
      'B12',
      'A13',
      'B13',
      'A14',
      'B14',
    ].forEach((cell) => {
      if (worksheet[cell]) {
        const isValueCell = cell.startsWith('B');
        worksheet[cell].s = isValueCell
          ? numberStyle
          : headerInfoStyle;
      }
    });

    // Estilo para el encabezado de detalle
    const detailHeaderRow = resumenData.length;
    const tableHeaderRow = detailHeaderRow + 1;
    worksheet[`A${detailHeaderRow}`].s =
      sectionHeaderStyle;

    // Merge cells
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } }, // Título principal
      { s: { r: 5, c: 0 }, e: { r: 5, c: 0 } }, // Estadísticas
      {
        s: { r: detailHeaderRow - 1, c: 0 },
        e: { r: detailHeaderRow - 1, c: 14 },
      }, // Detalle de registros
    ];

    // Aplicar estilos a los headers de la tabla de datos
    const columns = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
    ];
    columns.forEach((col) => {
      const cell = `${col}${tableHeaderRow}`;
      if (worksheet[cell]) {
        worksheet[cell].s = statsHeaderStyle;
      }
    });

    // Aplicar estilos a los datos de la tabla
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (
      let row = tableHeaderRow + 1;
      row <= range.e.r;
      row++
    ) {
      columns.forEach((col, index) => {
        if (worksheet[`${col}${row}`]) {
          // Aplicar estilo especial a columnas numéricas
          if (
            [3, 5, 6, 7, 8, 9, 10, 13, 14].includes(index)
          ) {
            worksheet[`${col}${row}`].s = numberStyle;
          } else {
            worksheet[`${col}${row}`].s = dataStyle;
          }
        }
      });
    }

    // Configurar ancho de columnas
    worksheet['!cols'] = [
      { wch: 18 }, // Fecha
      { wch: 20 }, // Usuario
      { wch: 15 }, // Medio Pago 1
      { wch: 12 }, // Monto 1
      { wch: 15 }, // Medio Pago 2
      { wch: 12 }, // Monto 2
      { wch: 14 }, // Total Ventas
      { wch: 10 }, // Clientes
      { wch: 10 }, // Días
      { wch: 14 }, // Total Extras
      { wch: 14 }, // Embarcaciones
      { wch: 12 }, // Estado
      { wch: 18 }, // Fecha Anulación
      { wch: 14 }, // Monto Anulado
      { wch: 14 }, // Extras Anulados
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      nombreHoja
    );

    // Configurar propiedades del libro
    workbook.Props = {
      Title: `${prefijoTitulo} - ${periodoTitulo}`,

      Subject: 'Resumen de Registros Mensuales',
      Author: 'Driza - Gestión de Campings',
      Manager: 'Departamento de Operaciones',
      Company: 'Marina Club Náutico',
      CreatedDate: new Date(),
    };

    XLSX.writeFile(
      workbook,
      nombreArchivo
    );

    // Mostrar notificación de éxito (opcional)
    console.log(
      `Excel generado exitosamente: ${nombreArchivo}`
    );
  };

  const construirMetricasPeriodoRegistros = (
    registrosFuente = []
  ) => {
    const totalRegistros = registrosFuente.reduce(
      (acc, dia) =>
        acc +
        (Array.isArray(dia.registros)
          ? dia.registros.length
          : 0),
      0
    );
    const totalVentasActivas = registrosFuente.reduce(
      (acc, dia) => acc + Number(dia.totalVentas || 0),
      0
    );
    const totalVentasAnuladas = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalVentasAnulados || 0),
      0
    );
    const totalClientesActivos = registrosFuente.reduce(
      (acc, dia) => acc + Number(dia.totalClientes || 0),
      0
    );
    const totalClientesAnulados = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalClientesAnulados || 0),
      0
    );
    const totalDiasActivos = registrosFuente.reduce(
      (acc, dia) => acc + Number(dia.totalDias || 0),
      0
    );
    const totalDiasAnulados = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalDiasAnulados || 0),
      0
    );
    const totalExtrasActivos = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalExtras || 0),
      0
    );
    const totalExtrasAnulados = registrosFuente.reduce(
      (acc, dia) =>
        acc + Number(dia.totalExtrasAnulados || 0),
      0
    );
    const totalesFinancierosPeriodo =
      combinarTotalesFinancieros(registrosFuente);
    const mediosPeriodo =
      combinarDesglosesMedios(registrosFuente);

    return {
      totalRegistros,
      totalVentasActivas,
      totalVentasAnuladas,
      totalClientesActivos,
      totalClientesAnulados,
      totalDiasActivos,
      totalDiasAnulados,
      totalExtrasActivos,
      totalExtrasAnulados,
      totalesFinancierosPeriodo,
      mediosPeriodo,
    };
  };

  const construirFilaTotalesPorDia = (dia) => {
    const fechaFormateada = dia.fecha
      ? format(parseISO(dia.fecha), 'dd/MM/yyyy')
      : '-';
    return {
      Fecha: fechaFormateada,
      Registros: Array.isArray(dia.registros)
        ? dia.registros.length
        : 0,
      'Clientes activos': Number(dia.totalClientes || 0),
      'Clientes anulados': Number(
        dia.totalClientesAnulados || 0
      ),
      'Días activos': Number(dia.totalDias || 0),
      'Días anulados': Number(
        dia.totalDiasAnulados || 0
      ),
      'Extras activos': Number(dia.totalExtras || 0),
      'Extras anulados': Number(
        dia.totalExtrasAnulados || 0
      ),
      Entradas: Number(
        dia.totalesFinancieros?.entradas || 0
      ),
      'Total general': Number(
        dia.totalesFinancieros?.totalGeneral ||
          dia.totalVentas ||
          0
      ),
      'Efectivo total': Number(
        dia.desgloseMedios?.totales?.efectivo?.total ||
          0
      ),
      'Efectivo entradas': Number(
        dia.desgloseMedios?.totales?.efectivo
          ?.entradas || 0
      ),
      'Efectivo extras': Number(
        dia.desgloseMedios?.totales?.efectivo?.extras ||
          0
      ),
      'Otros medios total': Number(
        dia.desgloseMedios?.totales?.otros?.total ||
          dia.desgloseMedios?.totales?.otrosMedios
            ?.total ||
          0
      ),
      'Otros medios entradas': Number(
        dia.desgloseMedios?.totales?.otros?.entradas ||
          dia.desgloseMedios?.totales?.otrosMedios
            ?.entradas ||
          0
      ),
      'Otros medios extras': Number(
        dia.desgloseMedios?.totales?.otros?.extras ||
          dia.desgloseMedios?.totales?.otrosMedios
            ?.extras ||
          0
      ),
    };
  };

  const generarInformeRegistrosResumido = ({
    registrosFuente,
    periodoTitulo,
    nombreArchivo,
    prefijoTitulo,
  }) => {
    if (!Array.isArray(registrosFuente) || registrosFuente.length === 0) {
      return;
    }

    const {
      totalRegistros,
      totalVentasActivas,
      totalVentasAnuladas,
      totalClientesActivos,
      totalClientesAnulados,
      totalDiasActivos,
      totalDiasAnulados,
      totalExtrasActivos,
      totalExtrasAnulados,
      totalesFinancierosPeriodo,
      mediosPeriodo,
    } = construirMetricasPeriodoRegistros(registrosFuente);

    const formatCurrency = (value) =>
      `$${Number(value || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
      })}`;

    const resumenData = [
      [prefijoTitulo, '', '', ''],
      [],
      ['Período', periodoTitulo, '', ''],
      [
        'Fecha de Generación',
        format(new Date(), 'dd/MM/yyyy HH:mm'),
        '',
        '',
      ],
      [],
      ['Métrica', 'Valor', '', ''],
      ['Total registros', totalRegistros, '', ''],
      [
        'Total ventas activas',
        formatCurrency(totalVentasActivas),
        '',
        '',
      ],
      [
        'Total ventas anuladas',
        formatCurrency(totalVentasAnuladas),
        '',
        '',
      ],
      [
        'Total clientes activos',
        totalClientesActivos,
        '',
        '',
      ],
      [
        'Total clientes anulados',
        totalClientesAnulados,
        '',
        '',
      ],
      ['Total días activos', totalDiasActivos, '', ''],
      ['Total días anulados', totalDiasAnulados, '', ''],
      [
        'Total extras activos',
        formatCurrency(totalExtrasActivos),
        '',
        '',
      ],
      [
        'Total extras anulados',
        formatCurrency(totalExtrasAnulados),
        '',
        '',
      ],
      [
        'Ingresos por entradas',
        formatCurrency(totalesFinancierosPeriodo.entradas),
        '',
        '',
      ],
      [
        'Ingresos por extras',
        formatCurrency(totalesFinancierosPeriodo.extras),
        '',
        '',
      ],
      [
        'Total general',
        formatCurrency(
          totalesFinancierosPeriodo.totalGeneral
        ),
        '',
        '',
      ],
      [],
      [
        'Totales consolidados por medio de pago',
        '',
        '',
        '',
      ],
      ['Medio', 'Total', 'Entradas', 'Extras'],
      ...(mediosPeriodo.detalle || []).map((medio) => [
        medio.nombre,
        formatCurrency(medio.total || medio.monto || 0),
        formatCurrency(medio.entradasMonto || 0),
        formatCurrency(
          medio.extrasMonto || medio.extras || 0
        ),
      ]),
      [
        'Efectivo',
        formatCurrency(
          mediosPeriodo.totales?.efectivo?.total || 0
        ),
        formatCurrency(
          mediosPeriodo.totales?.efectivo?.entradas || 0
        ),
        formatCurrency(
          mediosPeriodo.totales?.efectivo?.extras || 0
        ),
      ],
      [
        'Otros medios',
        formatCurrency(
          mediosPeriodo.totales?.otros?.total || 0
        ),
        formatCurrency(
          mediosPeriodo.totales?.otros?.entradas || 0
        ),
        formatCurrency(
          mediosPeriodo.totales?.otros?.extras || 0
        ),
      ],
    ];

    const filasPorDia = registrosFuente.map((dia) =>
      construirFilaTotalesPorDia(dia)
    );
    const headersTotalesDia = Object.keys(
      filasPorDia[0] || {}
    );
    const dataTotalesDia = [
      headersTotalesDia,
      ...filasPorDia.map((fila) =>
        headersTotalesDia.map((key) => fila[key])
      ),
    ];

    const resumenSheet = XLSX.utils.aoa_to_sheet(
      resumenData
    );
    resumenSheet['!cols'] = [
      { wch: 42 },
      { wch: 24 },
      { wch: 20 },
      { wch: 20 },
    ];

    const totalesDiaSheet = XLSX.utils.aoa_to_sheet(
      dataTotalesDia
    );
    totalesDiaSheet['!cols'] = [
      { wch: 12 },
      { wch: 10 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 18 },
      { wch: 16 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      resumenSheet,
      'Resumen'
    );
    XLSX.utils.book_append_sheet(
      workbook,
      totalesDiaSheet,
      'Totales por día'
    );

    workbook.Props = {
      Title: `${prefijoTitulo} - ${periodoTitulo}`,
      Subject: 'Resumen de Registros',
      Author: 'Driza - Gestión de Campings',
      Manager: 'Departamento de Operaciones',
      Company: 'Marina Club Náutico',
      CreatedDate: new Date(),
    };

    XLSX.writeFile(workbook, nombreArchivo);

    console.log(
      `Excel resumido generado exitosamente: ${nombreArchivo}`
    );
  };

  const descargarInformeMensual = () => {
    const mesFormateado = format(
      new Date(selectedYear, selectedMonth - 1, 1),
      'yyyy-MM'
    );

    generarInformeRegistros({
      registrosFuente: registros,
      periodoTitulo: format(
        new Date(selectedYear, selectedMonth - 1, 1),
        'MMMM yyyy',
        { locale: es }
      ),
      nombreArchivo: `registros_completo_${mesFormateado}.xlsx`,
      nombreHoja: 'Resumen Mensual',
      prefijoTitulo: 'RESUMEN MENSUAL DE REGISTROS',
    });
  };

  const descargarInformeRango = () => {
    if (rangoIncompleto || rangoInvalido || registrosEnRango.length === 0) {
      return;
    }

    const periodoTitulo = `${format(parseISO(fechaInicioRango), 'dd/MM/yyyy')} - ${format(
      parseISO(fechaFinRango),
      'dd/MM/yyyy'
    )}`;

    generarInformeRegistros({
      registrosFuente: registrosEnRango,
      periodoTitulo,
      nombreArchivo: `registros_completo_${fechaInicioRango}_a_${fechaFinRango}.xlsx`,
      nombreHoja: 'Resumen por Rango',
      prefijoTitulo: 'RESUMEN DE REGISTROS POR RANGO',
    });
  };

  const descargarInformeMensualResumido = () => {
    const mesFormateado = format(
      new Date(selectedYear, selectedMonth - 1, 1),
      'yyyy-MM'
    );

    generarInformeRegistrosResumido({
      registrosFuente: registros,
      periodoTitulo: format(
        new Date(selectedYear, selectedMonth - 1, 1),
        'MMMM yyyy',
        { locale: es }
      ),
      nombreArchivo: `registros_resumen_${mesFormateado}.xlsx`,
      prefijoTitulo: 'RESUMEN MENSUAL DE REGISTROS',
    });
  };

  const descargarInformeRangoResumido = () => {
    if (rangoIncompleto || rangoInvalido || registrosEnRango.length === 0) {
      return;
    }

    const periodoTitulo = `${format(parseISO(fechaInicioRango), 'dd/MM/yyyy')} - ${format(
      parseISO(fechaFinRango),
      'dd/MM/yyyy'
    )}`;

    generarInformeRegistrosResumido({
      registrosFuente: registrosEnRango,
      periodoTitulo,
      nombreArchivo: `registros_resumen_${fechaInicioRango}_a_${fechaFinRango}.xlsx`,
      prefijoTitulo: 'RESUMEN DE REGISTROS POR RANGO',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B4F72] to-[#3498DB] text-[#FAFAFA] p-4 md:p-8">
      <motion.header
        className="mb-8 text-center"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-[#F1C40F] mb-2">
          Histórico de Registros Diarios
        </h1>
        <p className="text-xl text-[#BDC3C7]">
          Resumen de todos los días (excepto hoy)
        </p>
      </motion.header>

      {/* Filtros */}
      <Card className="bg-[#1B4F72] bg-opacity-50 p-4 mb-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="text-[#F1C40F]" />
            <span className="text-lg">Filtrar por:</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <select
              value={selectedYear}
              onChange={(e) =>
                setSelectedYear(parseInt(e.target.value))
              }
              className="bg-[#3498DB] bg-opacity-10 text-[#FAFAFA] border-none rounded-md p-2"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <div className="flex items-center space-x-2">
              <Button
                onClick={() => cambiarMes(-1)}
                className="button-secondary"
              >
                <ChevronLeft />
              </Button>

              <span className="text-xl min-w-[120px] text-center">
                {format(
                  new Date(selectedYear, selectedMonth - 1, 1),
                  'MMMM',
                  { locale: es }
                )}
              </span>

              <Button
                onClick={() => cambiarMes(1)}
                className="button-secondary"
              >
                <ChevronRight />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fechaInicioRango}
                onChange={(e) => setFechaInicioRango(e.target.value)}
                className="bg-[#3498DB] bg-opacity-10 text-[#FAFAFA] rounded-md px-3 py-2"
                aria-label="Fecha inicio rango"
              />
              <span className="text-[#BDC3C7]">a</span>
              <input
                type="date"
                value={fechaFinRango}
                onChange={(e) => setFechaFinRango(e.target.value)}
                className="bg-[#3498DB] bg-opacity-10 text-[#FAFAFA] rounded-md px-3 py-2"
                aria-label="Fecha fin rango"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={descargarInformeMensual}
              className="button-yellow-gold"
              disabled={loading || registros.length === 0}
            >
              <Download className="mr-2" />
              Descargar completo mes
            </Button>
            <Button
              onClick={descargarInformeMensualResumido}
              className="button-yellow-gold"
              disabled={loading || registros.length === 0}
            >
              <Download className="mr-2" />
              Descargar resumen mes
            </Button>
            <Button
              onClick={descargarInformeRango}
              className="button-yellow-gold"
              disabled={
                loading ||
                registros.length === 0 ||
                rangoIncompleto ||
                rangoInvalido ||
                registrosEnRango.length === 0
              }
              title={
                rangoInvalido
                  ? 'El rango de fechas es inválido'
                  : 'Descargar por rango de fechas'
              }
            >
              <Download className="mr-2" />
              Descargar completo rango
            </Button>
            <Button
              onClick={descargarInformeRangoResumido}
              className="button-yellow-gold"
              disabled={
                loading ||
                registros.length === 0 ||
                rangoIncompleto ||
                rangoInvalido ||
                registrosEnRango.length === 0
              }
              title={
                rangoInvalido
                  ? 'El rango de fechas es inválido'
                  : 'Descargar por rango de fechas'
              }
            >
              <Download className="mr-2" />
              Descargar resumen rango
            </Button>
          </div>
        </div>
        {rangoInvalido && (
          <p className="text-red-300 text-sm mt-2">
            El rango seleccionado es inválido: la fecha de inicio no puede ser mayor a la fecha de fin.
          </p>
        )}
      </Card>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-xl">Cargando registros...</p>
        </div>
      ) : registros.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="mx-auto w-12 h-12 text-[#BDC3C7] mb-4" />
          <p className="text-xl text-[#BDC3C7]">
            No hay registros para el mes seleccionado
          </p>
        </div>
      ) : (
        <>
          {/* Resumen del Mes */}
          <Card className="bg-[#27AE60] bg-opacity-10 p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4 text-center text-[#2ECC71]">
              Resumen del Mes -{' '}
              {format(
                new Date(selectedYear, selectedMonth - 1, 1),
                'MMMM yyyy',
                { locale: es }
              )}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  <Users className="mr-2" /> Total Clientes
                </p>
                <p className="text-3xl font-bold text-[#3498DB]">
                  {resumenMes.totalClientes || 0}
                </p>
                {resumenMes.totalClientesAnulados > 0 && (
                  <p className="text-sm text-red-300 mt-1">
                    Anuladas: {resumenMes.totalClientesAnulados}
                  </p>
                )}
              </div>

              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  <DollarSign className="mr-2" /> Entradas
                </p>
                <p className="text-3xl font-bold text-[#F1C40F]">
                  $
                  {Number(
                    totalesFinancierosResumen.entradas || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
                <p className="text-sm text-[#BDC3C7]">
                  Sin extras incluidos
                </p>
              </div>

              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  <Wallet className="mr-2" /> Total ingresos
                </p>
                <p className="text-3xl font-bold text-[#F1C40F]">
                  $
                  {Number(
                    totalesFinancierosResumen.totalGeneral ||
                      resumenMes.totalVentas ||
                      0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
                {resumenMes.totalVentasAnulados > 0 && (
                  <p className="text-sm text-red-300 mt-1">
                    Anuladas: $
                    {Number(
                      resumenMes.totalVentasAnulados || 0
                    ).toLocaleString('es-AR', {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                )}
              </div>

              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  <Anchor className="mr-2" /> Total Embarcaciones
                </p>
                <p className="text-3xl font-bold text-[#2ECC71]">
                  {resumenMes.totalEmbarcaciones || 0}
                </p>
              </div>

              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  Total Días vendidos
                </p>
                <p className="text-3xl font-bold text-[#00F7FF]">
                  {Number(
                    resumenMes.totalDias || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 0,
                  })}
                </p>
                {Number(resumenMes.totalDiasAnulados || 0) >
                  0 && (
                  <p className="text-sm text-red-300 mt-1">
                    Anulados:{' '}
                    {Number(
                      resumenMes.totalDiasAnulados || 0
                    ).toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </p>
                )}
              </div>

              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  Total Extras
                </p>
                <p className="text-3xl font-bold text-[#F39C12]">
                  $
                  {Number(
                    resumenMes.totalExtras || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
                <p className="text-sm text-[#BDC3C7]">
                  Ingresos por extras: $
                  {Number(
                    totalesFinancierosResumen.extras || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
                {Number(
                  resumenMes.totalExtrasAnulados || 0
                ) > 0 && (
                  <p className="text-sm text-red-300 mt-1">
                    Anulados: $
                    {Number(
                      resumenMes.totalExtrasAnulados || 0
                    ).toLocaleString('es-AR', {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  <DollarSign className="mr-2" /> Total en
                  efectivo
                </p>
                <p className="text-2xl font-bold text-[#F1C40F]">
                  $
                  {Number(
                    efectivoResumenMes.total || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
                <p className="text-sm text-[#BDC3C7]">
                  Entradas: $
                  {Number(
                    efectivoResumenMes.entradas || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}{' '}
                  · Extras: $
                  {Number(
                    efectivoResumenMes.extras || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="text-center bg-[#1B4F72] bg-opacity-30 p-4 rounded-lg">
                <p className="text-lg mb-2 text-[#BDC3C7] flex items-center justify-center">
                  <CreditCard className="mr-2" /> Otros medios
                </p>
                <p className="text-2xl font-bold text-[#3498DB]">
                  $
                  {Number(
                    otrosResumenMes.total || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
                <p className="text-sm text-[#BDC3C7]">
                  Entradas: $
                  {Number(
                    otrosResumenMes.entradas || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}{' '}
                  · Extras: $
                  {Number(
                    otrosResumenMes.extras || 0
                  ).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          </Card>

          {/* Días del mes */}
          <div className="space-y-4">
            {registros.map((dia) => (
              <DiaRegistro
                key={dia.fecha}
                dia={dia}
                onDownload={descargarInformeDiario}
                onShowBreakdown={fetchVentasPorUsuarioMedioPago}
              />
            ))}
          </div>
        </>
      )}

      {/* Modal de desglose */}
      <AnimatePresence>
        {modalAbierto && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              className="bg-[#1B4F72] p-6 rounded-lg w-[95vw] max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <h3 className="text-xl font-bold text-[#F1C40F] mb-4">
                Desglose de ventas:{' '}
                {usuarioSeleccionadoDesglose?.nombre ||
                  'Usuario desconocido'}
              </h3>

              <div className="mb-4 flex-1 overflow-auto">
                <div className="grid grid-cols-9 gap-3 font-semibold border-b border-[#BDC3C7] border-opacity-20 pb-2 mb-2 text-sm md:text-base min-w-[900px]">
                  <div>Medio de Pago</div>
                  <div className="text-right">
                    Entradas
                  </div>
                  <div className="text-right">Días</div>
                  <div className="text-right">Monto</div>
                  <div className="text-right">Extras</div>
                  <div className="text-right text-red-300">
                    Entradas Anuladas
                  </div>
                  <div className="text-right text-red-300">
                    Días Anulados
                  </div>
                  <div className="text-right text-red-300">
                    Monto Anulado
                  </div>
                  <div className="text-right text-red-300">
                    Extras Anulados
                  </div>
                </div>

                {ventasUsuarioMedioPago.detalle?.length > 0 ? (
                  <>
                    {ventasUsuarioMedioPago.detalle.map(
                      (medio, index) => (
                        <motion.div
                          key={index}
                          className="grid grid-cols-9 gap-3 items-center py-2 px-4 hover:bg-[#1B4F72] hover:bg-opacity-20 rounded-lg text-sm md:text-base min-w-[900px]"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            delay: index * 0.05,
                          }}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-[#2ECC71] mr-2"></div>
                            <span>{medio.nombre}</span>
                          </div>
                          <div className="text-right font-medium">
                            {medio.entradas || 0}
                          </div>
                          <div className="text-right font-medium">
                            {Number(
                              medio.dias || 0
                            ).toLocaleString('es-AR', {
                              minimumFractionDigits: 0,
                            })}
                          </div>
                          <div className="text-right font-bold text-[#F1C40F]">
                            $
                            {Number(
                              medio.monto || 0
                            ).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div className="text-right font-bold text-[#F1C40F]">
                            $
                            {Number(
                              medio.extras || 0
                            ).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div className="text-right font-medium text-red-300">
                            {medio.entradasAnuladas || 0}
                          </div>
                          <div className="text-right font-medium text-red-300">
                            {Number(
                              medio.diasAnulados || 0
                            ).toLocaleString('es-AR', {
                              minimumFractionDigits: 0,
                            })}
                          </div>
                          <div className="text-right font-bold text-red-300">
                            $
                            {Number(
                              medio.montoAnulado || 0
                            ).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div className="text-right font-bold text-red-300">
                            $
                            {Number(
                              medio.extrasAnulados || 0
                            ).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                        </motion.div>
                      )
                    )}

                    <div className="bg-[#1B4F72] bg-opacity-50 rounded-lg p-4 mt-4 sticky bottom-0 min-w-[900px]">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center text-sm md:text-base">
                        <div className="font-semibold">
                          Total Entradas
                        </div>
                        <div className="text-right font-bold">
                          {ventasUsuarioMedioPago.totales
                            ?.entradas || 0}
                        </div>
                        <div className="font-semibold text-right">
                          Total Días
                        </div>
                        <div className="text-right font-bold">
                          {Number(
                            ventasUsuarioMedioPago
                              .totales?.dias || 0
                          ).toLocaleString('es-AR', {
                            minimumFractionDigits: 0,
                          })}
                        </div>
                        <div className="font-semibold text-right">
                          Total Recaudado
                        </div>
                        <div className="text-right font-bold text-[#F1C40F]">
                          $
                          {Number(
                            ventasUsuarioMedioPago
                              .totales?.montoTotal ||
                              ventasUsuarioMedioPago
                                .totales?.total ||
                              0
                          ).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                        <div className="font-semibold text-right">
                          Total Extras
                        </div>
                        <div className="text-right font-bold text-[#F1C40F]">
                          $
                          {Number(
                            ventasUsuarioMedioPago
                              .totales?.extras || 0
                          ).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                        <div className="font-semibold text-right text-red-300">
                          Entradas Anuladas
                        </div>
                        <div className="text-right font-bold text-red-300">
                          {ventasUsuarioMedioPago.totales
                            ?.entradasAnuladas || 0}
                        </div>
                        <div className="font-semibold text-right text-red-300">
                          Días Anulados
                        </div>
                        <div className="text-right font-bold text-red-300">
                          {Number(
                            ventasUsuarioMedioPago
                              .totales?.diasAnulados ||
                              0
                          ).toLocaleString('es-AR', {
                            minimumFractionDigits: 0,
                          })}
                        </div>
                        <div className="font-semibold text-right text-red-300">
                          Monto Anulado
                        </div>
                        <div className="text-right font-bold text-red-300">
                          $
                          {Number(
                            ventasUsuarioMedioPago
                              .totales?.totalAnulado ||
                              ventasUsuarioMedioPago
                                .totales?.montoAnulado ||
                              0
                          ).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                        <div className="font-semibold text-right text-red-300">
                          Extras Anulados
                        </div>
                        <div className="text-right font-bold text-red-300">
                          $
                          {Number(
                            ventasUsuarioMedioPago
                              .totales
                              ?.extrasAnulados || 0
                          ).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6 text-[#BDC3C7]">
                    <Ticket className="mx-auto mb-3" size={32} />
                    <p>No se encontraron transacciones</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end border-t border-[#BDC3C7] border-opacity-20 pt-4">
                <button
                  onClick={() => setModalAbierto(false)}
                  className="px-4 py-2 bg-[#F1C40F] text-[#1B4F72] rounded-md hover:bg-[#D4AC0D] transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .button-primary {
          background-color: #3498db;
          color: #fafafa;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          transition: background-color 0.2s;
        }
        .button-primary:hover {
          background-color: #1b4f72;
        }
        .button-secondary {
          background-color: #2ecc71;
          color: #fafafa;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          transition: background-color 0.2s;
        }
        .button-secondary:hover {
          background-color: #27ae60;
        }
        .button-yellow-gold {
          background-color: #f1c40f;
          color: #1a1a1a;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          transition: background-color 0.2s;
        }
        .button-yellow-gold:hover {
          background-color: #d4ac0d;
        }
      `}</style>
    </div>
  );
};

export default HistoricoRegistros;
